import { Buffer } from "buffer";
import jsSHA from "jssha";

export const encryptData = async <T>(
	data: T,
	secret: string,
): Promise<string> => {
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const keyBytes = Uint8Array.from(atob(secret), (c) => c.charCodeAt(0));
	const key = await crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, [
		"encrypt",
	]);

	const encoded = new TextEncoder().encode(JSON.stringify(data));
	const ciphertext = await crypto.subtle.encrypt(
		{ name: "AES-GCM", iv },
		key,
		encoded,
	);

	const buf = new Uint8Array(ciphertext);
	const encrypted = buf.slice(0, -16);
	const authTag = buf.slice(-16);

	const toHex = (bytes: Uint8Array) =>
		Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

	const toBase64url = (bytes: Uint8Array) =>
		btoa(String.fromCharCode(...bytes))
			.replace(/\+/g, "-")
			.replace(/\//g, "_")
			.replace(/=+$/, "");

	return `${toBase64url(iv)}.${toHex(encrypted)}.${toHex(authTag)}`;
};

export const LOWERCASE_VOCAB = "abcdefghijklmnopqrstuvwxyz";

const PERIOD: number = 6;

export const getOtpFromKey = (HASHED_KEY: string): string => {
	const epoch = Math.round(Date.now() / 1000);
	const time = Math.floor(epoch / PERIOD);
	const buf = Buffer.allocUnsafe(8);

	buf.writeBigInt64BE(BigInt(time));

	const sha = new jsSHA("SHA-256", "ARRAYBUFFER");
	sha.setHMACKey(HASHED_KEY, "HEX");
	sha.update(buf);

	const hmac = sha.getHMAC("UINT8ARRAY");

	const periodHashIndex = hmac[hmac.length - 1] & 15;
	const maxLong = 9223372036854775807n;
	const slice = fromBigEndianArray(hmac, periodHashIndex);

	const periodHashSlice = slice & maxLong;

	const limitValue = BigInt(26 ** 8);
	const trimmedOtp = periodHashSlice % limitValue;

	return toBase26(trimmedOtp);
};

const fromBigEndianArray = (array: Uint8Array, startIndex: number): bigint => {
	const buf = Buffer.from(array.slice(startIndex, startIndex + 8));

	return buf.readBigUInt64BE();
};

const toBase26 = (value: bigint): string => {
	let next = value;
	const result: string[] = [];
	const volume: bigint = BigInt(LOWERCASE_VOCAB.length);

	for (;;) {
		const left = next % volume;
		next = (next - left) / volume;
		result.unshift(LOWERCASE_VOCAB[Number(left)]);

		if (Number(next) === 0) {
			break;
		}
	}

	return result.join("");
};
